import React, { useCallback, useEffect, useState, memo } from 'react';
import { useGPSLost } from './GPSLostContext';
import { shapeToVectorLayer, useLayers } from '@ais-ui/react-openlayers';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Icons,
  LoadingSpinner,
  ScrollArea,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { singleTrackToGeoJSON } from './trackToSeparateGeoJSON';
import { useMapElementPositioning } from './useMapElementPositioning';
import {
  AutoCompleteShipInfo,
  ExtractedTargetInfo,
  extractTargetInfos,
  getTargetInfoByMMSI,
  ShipInfo,
  useSession,
} from '@ais-ui/websockets';
import {
  PiBoat,
  PiCellSignalSlash,
  PiLineSegments,
  PiWarning,
  PiX,
} from 'react-icons/pi';
import { cn, useTimeZoneContext } from '@ais-ui/utils';
import { useGetGridJobTracks } from '@ais-ui/api';
import { useAISMapContext } from '../AISMapContext';

type ShipData = ExtractedTargetInfo | number;

interface GPSLostShipItemProps {
  shipData: ShipData;
  onTrackClick: (mmsi: number) => void;
  isTrackLoading: boolean;
  selectedMmsis: number[];
}

const GPSLostShipItem = memo(
  ({
    shipData,
    onTrackClick,
    isTrackLoading,
    selectedMmsis,
  }: GPSLostShipItemProps) => {
    const { setZoomToShip } = useAISMapContext();

    let mmsi;
    let name;
    let imo;
    let callSign = null;

    if (typeof shipData === 'number') {
      mmsi = shipData;
      name = 'Unknown';
      imo = 'Unknown';
    } else {
      const ship = shipData.extractedTargetInfo[0] as ShipInfo;
      mmsi = ship.mmsi;
      name = ship.name;
      imo = ship.imo;
      callSign = ship.callSign;
    }

    const isTrackSelected = selectedMmsis.includes(Number(mmsi));

    const handleTrackButtonClick = () => {
      // Convert mmsi to number if it's a string
      const mmsiNumber = typeof mmsi === 'string' ? parseInt(mmsi, 10) : mmsi;
      onTrackClick(mmsiNumber);
    };

    const handleGoToShip = () => {
      const mmsiString = typeof mmsi === 'number' ? mmsi.toString() : mmsi;
      onGoToTarget(mmsiString, name, imo, callSign);
    };

    const onGoToTarget = useCallback(
      (mmsi: string, name: string, imo: string, callSign: string | null) => {
        // The backend sets the mmsi as the name in some cases, and if we try to search by mmsi name, it'll not be found
        const mmsiName = name !== mmsi ? name : '';
        const autoCompleteShip: AutoCompleteShipInfo = {
          name: mmsiName,
          mmsi: mmsi,
          imo: imo,
          callSign: callSign,
          type: 'TT_SHIP',
        };
        setZoomToShip(autoCompleteShip);
      },
      [setZoomToShip]
    );

    return (
      <div className="p-3">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h3 className="font-medium text-base flex items-center">{name}</h3>
            <div className="text-sm text-gray-500 flex items-center space-x-2">
              <span>MMSI: {mmsi}</span>
              <div className={cn('w-1.5 h-1.5 rounded-full bg-blue-500')}></div>
              {imo && <span>IMO: {imo}</span>}
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="smIcon"
                  variant="ghost"
                  onClick={handleTrackButtonClick}
                  disabled={isTrackLoading}
                >
                  {isTrackLoading ? (
                    <Icons.spinner className="h-4 w-4 animate-spin" />
                  ) : isTrackSelected ? (
                    <PiX className="h-4 w-4" />
                  ) : (
                    <PiLineSegments className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isTrackLoading
                  ? 'Loading track...'
                  : isTrackSelected
                  ? 'Remove track'
                  : 'Show track'}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="smIcon" variant="ghost" onClick={handleGoToShip}>
                  <PiBoat className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Select ship</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  }
);

interface GPSListShipListProps {
  shipData: ShipData[];
}

const GPSListShipList = memo(({ shipData }: GPSListShipListProps) => {
  const { selectedGPSLostGridCell, selectedMmsis, setSelectedMmsis } =
    useGPSLost();
  const { addLayer, removeLayerById } = useLayers();
  const getGridJobTracks = useGetGridJobTracks();
  const { timeZone } = useTimeZoneContext();

  // Add state to track loading state for each track button
  const [loadingTracks, setLoadingTracks] = useState<Record<string, boolean>>(
    {}
  );

  const handleTrackClick = useCallback(
    async (mmsi: number) => {
      try {
        if (selectedMmsis.includes(mmsi)) {
          // Remove this MMSI from selectedMmsis
          setSelectedMmsis((prev) => prev.filter((id) => id !== mmsi));
          removeLayerById(`gps-lost-track-point-${mmsi}`);
          removeLayerById(`gps-lost-track-linestring-${mmsi}`);
          return;
        }

        setLoadingTracks((prev) => ({ ...prev, [mmsi]: true }));

        if (!selectedGPSLostGridCell) {
          console.error('No selected GPS lost grid cell');
          return;
        }

        const { configurationId, instanceId } = selectedGPSLostGridCell;
        const trackData = await getGridJobTracks(
          configurationId,
          instanceId,
          mmsi
        );
        const track = {
          mmsi: mmsi,
          data: trackData.items,
        };

        // Convert the track data to GeoJSON
        const { lineGeoJSON, pointsGeoJSON } = singleTrackToGeoJSON(track);

        const trackPointLayer = shapeToVectorLayer(pointsGeoJSON, timeZone);
        const trackLineLayer = shapeToVectorLayer(lineGeoJSON, timeZone);

        addLayer(
          trackLineLayer,
          `GPS Lost Track Point ${mmsi}`,
          true,
          true,
          true,
          {},
          `gps-lost-track-linestring-${mmsi}`,
          true
        );
        addLayer(
          trackPointLayer,
          `GPS Lost Track Point ${mmsi}`,
          true,
          true,
          true,
          {},
          `gps-lost-track-point-${mmsi}`,
          true
        );
        setSelectedMmsis((prev) => {
          return [...prev, mmsi];
        });
      } catch (error) {
        console.error(
          `Failed to fetch or convert track for MMSI ${mmsi}:`,
          error
        );
      } finally {
        setLoadingTracks((prev) => ({ ...prev, [mmsi]: false }));
      }
    },
    [
      selectedMmsis,
      removeLayerById,
      selectedGPSLostGridCell,
      getGridJobTracks,
      addLayer,
      setSelectedMmsis,
    ]
  );

  return (
    <ScrollArea className="h-[300px]">
      {shipData.map((shipData) => (
        <GPSLostShipItem
          key={
            typeof shipData === 'number'
              ? shipData
              : (shipData.extractedTargetInfo[0] as ShipInfo).mmsi
          }
          shipData={shipData}
          onTrackClick={handleTrackClick}
          isTrackLoading={
            typeof shipData === 'number'
              ? !!loadingTracks[shipData]
              : !!loadingTracks[
                  (shipData.extractedTargetInfo[0] as ShipInfo).mmsi
                ]
          }
          selectedMmsis={selectedMmsis}
        />
      ))}
    </ScrollArea>
  );
});

export function GPSLostLayer() {
  const {
    clickCoordinate,
    selectedGPSLostGridCell,
    setClickCoordinate,
    setSelectedGPSLostGridCell,
  } = useGPSLost();
  const [shipData, setShipData] = useState<ShipData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { olSession } = useSession();

  const deselectGridCell = useCallback(() => {
    setClickCoordinate(null);
    setSelectedGPSLostGridCell(null);
    setShipData([]);
    setError(null);
  }, [setClickCoordinate, setSelectedGPSLostGridCell]);

  useEffect(() => {
    setShipData([]);
    setError(null);
  }, [selectedGPSLostGridCell]);

  useEffect(() => {
    if (
      !selectedGPSLostGridCell ||
      selectedGPSLostGridCell.mmsis.length === 0
    ) {
      return;
    }

    const shipDataArray: ShipData[] = [];
    setIsLoading(true);
    setError(null);

    // Track how many requests have completed
    let completedRequests = 0;
    const totalRequests = selectedGPSLostGridCell.mmsis.length;

    // Iterate through each MMSI in the selectedGPSLostGridCell
    selectedGPSLostGridCell.mmsis.forEach(async (mmsi) => {
      try {
        const queryPointByMmsi = await getTargetInfoByMMSI(olSession, mmsi);
        const targetInfoResultByMmsi = extractTargetInfos(queryPointByMmsi);

        if (targetInfoResultByMmsi.extractedTargetInfo.length === 0) {
          shipDataArray.push(mmsi);
        } else {
          shipDataArray.push(targetInfoResultByMmsi);
        }
      } catch (error) {
        console.error(`Error fetching ship data for MMSI ${mmsi}:`, error);
        // We don't set the error state here to allow other requests to complete
      } finally {
        completedRequests++;

        // Once all requests are complete, update the state
        if (completedRequests === totalRequests) {
          setShipData(shipDataArray);
          setIsLoading(false);

          // If no data was fetched successfully, set an error
          if (shipDataArray.length === 0) {
            setError(new Error('Failed to fetch any ship data'));
          }
        }
      }
    });
  }, [olSession, selectedGPSLostGridCell]);

  const { setPopperElement, screenPosition, styles, isInViewport } =
    useMapElementPositioning(clickCoordinate);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center p-6 space-y-4">
          <LoadingSpinner size={'small'}></LoadingSpinner>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center p-6 space-y-4">
          <div className="rounded-full bg-red-100 p-3">
            <PiWarning className="h-6 w-6 text-red-500" />
          </div>
          <p className="text-sm text-gray-500">Failed to load ship data</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (selectedGPSLostGridCell) {
                setIsLoading(true);
                setError(null);
                // Re-trigger the effect by setting the coordinate again
                const temp = selectedGPSLostGridCell;
                setSelectedGPSLostGridCell(null);
                setTimeout(() => setSelectedGPSLostGridCell(temp), 100);
              }
            }}
          >
            Try Again
          </Button>
        </div>
      );
    }

    if (!shipData || shipData.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center p-6 space-y-4">
          <p className="text-sm text-gray-500">No ships with GPS Lost</p>
        </div>
      );
    }

    return <GPSListShipList shipData={shipData} />;
  };

  // Only render card if coordinate is in viewport
  return clickCoordinate && screenPosition && isInViewport ? (
    <Card
      ref={setPopperElement}
      style={{ ...styles, transition: 'none' }}
      className="mx-6 p-0 min-w-[350px] shadow-lg cursor-default"
    >
      <CardHeader className="p-3 flex flex-col justify-between relative space-y-1">
        <div className="flex items-center space-x-2">
          <PiCellSignalSlash className="h-5 w-5" />
          <CardTitle className="text-lg">GPS Lost</CardTitle>
        </div>
        {shipData.length > 0 && (
          <p className="text-muted-foreground text-sm">
            Ships that lost GPS: {shipData.length}
          </p>
        )}

        <Button
          size="icon"
          variant="ghost"
          className="absolute top-0 right-1"
          onClick={deselectGridCell}
        >
          <PiX className="h-4 w-4" />
        </Button>
      </CardHeader>
      <Separator />
      <CardContent className="p-0">{renderContent()}</CardContent>
    </Card>
  ) : null;
}
